
import React from 'react';
import type { FormValues } from '@/lib/types';
import { LAYOUT_ORDER, LAYOUT_ORDER_LEGACY, PASSIVE_TEST_DATA } from './constants';
import { TestData } from './types';
import TestInputCard from './TestInputCard';

interface TestInputGridProps {
    formValues: FormValues;
    onInputChange: (id: string, value: string) => void;
    onOpenVisioMeter?: (testKey: string, side: 'L' | 'R') => void;
    /** Optional override map for test data (nominals/units/etc.) */
    testDataMap?: TestData;
    /** Optional override for grid ordering (e.g. legacy layout). */
    layoutOrder?: (string | string[])[];
    /** Controls whether optional assessments are rendered. Defaults to true for backward compatibility. */
    showOptionalAssessments?: boolean;
}

const prioritizeOptionalRows = (layout: (string | string[])[], dataMap: TestData): (string | string[])[] => {
    const optionalRows: (string | string[])[] = [];
    const standardRows: (string | string[])[] = [];
    layout.forEach((item) => {
        if (isOptionalGroup(item, dataMap)) {
            optionalRows.push(item);
        } else {
            standardRows.push(item);
        }
    });
    return [...optionalRows, ...standardRows];
};

const isOptionalGroup = (item: string | string[], dataMap: TestData): boolean => {
    const keys = Array.isArray(item) ? item : [item];
    return keys.every(key => dataMap[key]?.optional === true);
};

const TestInputGrid: React.FC<TestInputGridProps> = ({ formValues, onInputChange, onOpenVisioMeter, testDataMap, layoutOrder, showOptionalAssessments = true }) => {
    const dataMap = testDataMap || PASSIVE_TEST_DATA;
    const isLegacyDataMap = Boolean(dataMap.HipAdductionObers) && !Boolean(dataMap.HipExtension);
    const effectiveLayoutOrder = layoutOrder || (isLegacyDataMap ? LAYOUT_ORDER_LEGACY : LAYOUT_ORDER);
    const visibleLayoutOrder = showOptionalAssessments
        ? prioritizeOptionalRows(effectiveLayoutOrder, dataMap)
        : effectiveLayoutOrder.filter((item) => !isOptionalGroup(item, dataMap));
    let optionalHeadingShown = false;

    const renderCard = (key: string) => {
        const testData = dataMap[key];
        if (!testData) return null;
        return (
            <TestInputCard
                key={key}
                testKey={key}
                testData={testData}
                formValues={formValues}
                onInputChange={onInputChange}
                onOpenVisioMeter={onOpenVisioMeter}
            />
        );
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
            {visibleLayoutOrder.map((item: string | string[], index: number) => {
                const showOptionalHeading = !optionalHeadingShown && isOptionalGroup(item, dataMap);
                if (showOptionalHeading) optionalHeadingShown = true;

                const heading = showOptionalHeading ? (
                    <div key="optional-heading" className="md:col-span-2 mt-4 mb-1">
                        <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Optional Assessments</h3>
                    </div>
                ) : null;

                if (Array.isArray(item)) {
                    return (
                        <React.Fragment key={`row-${index}`}>
                            {heading}
                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                                {item.map((key) => renderCard(key))}
                            </div>
                        </React.Fragment>
                    );
                } else {
                    const singleCard = renderCard(item);
                    if (!singleCard) return null;
                    return (
                        <React.Fragment key={item}>
                            {heading}
                            <div className="md:col-span-2 w-full">
                                {singleCard}
                            </div>
                        </React.Fragment>
                    );
                }
            })}
        </div>
    );
};

export default TestInputGrid;
